import { createContext } from "react";

import { saveAs } from "file-saver";
import { PhoneNumberFormat, PhoneNumberUtil } from "google-libphonenumber";
import moment, { Moment } from "moment-timezone";

import { CLIENT_LOGIN_METHOD } from "./constants";
import {
  ApiDownloadType,
  ApiRequestContentType,
  ApiRequestType,
  ErrorType,
} from "./types";

const PAYMENT_REQUIRED_EVENT = "payment-required";

const handlePaymentRequiredResponse = (response: Response | ErrorType) => {
  if (response.status !== 402) {
    return false;
  }

  localStorage.removeItem("token");
  localStorage.removeItem("user");
  window.dispatchEvent(new Event(PAYMENT_REQUIRED_EVENT));
  return true;
};

const apiRequest = ({
  method = "GET",
  endpoint,
  params = {},
  body = {},
  signal,
  contentType = "application/json",
}: ApiRequestType) => {
  const options: ApiRequestContentType = {
    method,
    headers: {
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
  };

  if (contentType !== "multipart/form-data") {
    options.headers["Content-Type"] = contentType;
  }

  if (Object.keys(params).length > 0) {
    endpoint += "?" + encodeURLParams(params);
  }

  if (contentType === "multipart/form-data") {
    options.body = body;
  } else if (method !== "GET" && body) {
    options.body = JSON.stringify(body);
  }

  if (signal) {
    options.signal = signal;
  }

  const apiUrl = `${process.env.REACT_APP_API_URL}/${endpoint}`;
  return new Promise((resolve, reject) => {
    fetch(apiUrl, options)
      .then((response) => {
        if (response.ok) {
          if (response.status === 204) {
            return resolve(null);
          }
          return resolve(response.json());
        }
        if (handlePaymentRequiredResponse(response)) {
          return;
        }
        if (response.status === 401 && endpoint !== "v2/sign-in") {
          clearAndLogout(customNavigate);
          return;
        }
        return reject(response);
      })
      .catch((error) => {
        return reject(error);
      });
  });
};

const apiDownload = async ({
  endpoint,
  params = {},
  mimetype,
  filename,
}: ApiDownloadType) => {
  const options: Pick<ApiRequestContentType, "headers"> = {
    headers: {
      Authorization: `Bearer ${localStorage.getItem("token")}`,
      Accept: mimetype,
    },
  };
  if (Object.keys(params).length > 0) {
    endpoint += "?" + encodeURLParams(params);
  }
  const apiUrl = `${process.env.REACT_APP_API_URL}/${endpoint}`;

  const response = await fetch(apiUrl, options);
  if (response.ok) {
    const saveAsFilename: any = filename
      ? filename
      : response.headers.get("x-suggested-filename");
    const data = await response.blob();
    saveAs(data, saveAsFilename);
  } else if (!handlePaymentRequiredResponse(response)) {
    alert(`Failed to download file: ${formatApiRequestError(response)}`);
  }
};

const clearAndLogout = (navigate: any) => {
  localStorage.removeItem("token");
  localStorage.removeItem("user");
  redirectToLogout(navigate);
};

const customNavigate = (path: any) =>
  window.location.assign(new URL(path, window.location.origin));

const encodeURLParams = (params: { [key: string]: any }) => {
  return Object.keys(params)
    .map((key) => {
      if (Array.isArray(params[key])) {
        return params[key]
          .map((value: string) => {
            return encodeURIComponent(key) + "=" + encodeURIComponent(value);
          })
          .join("&");
      } else {
        return encodeURIComponent(key) + "=" + encodeURIComponent(params[key]);
      }
    })
    .join("&");
};

const formatApiRequestError = (error: ErrorType) => {
  if (error.status) {
    return `${error.status} ${error.statusText} from ${error.url}`;
  }
  return error;
};

const formatMomentWithTimezone = (
  datetime: Moment,
  timezone: string,
  format: string,
  showTimezone = false
) => {
  /*
    helper function to determine if the given datetime's timezone is the same as user's local timezone, then format it.
    If timezones are the same or timezone not provided, convert to local timezone.
    If different, convert to given timezone (also add the timezone abbr)

    We can't just compare the timezone name, since some timezones are effectively
    the same but have different names (e.g., America/Halifax vs America/Moncton).
    Instead, we compare the datetime offsets of each timezone from UTC.
  */
  if (!datetime) {
    return "";
  }

  const deviceTimezone = moment.tz.guess(true);

  const { isDifferentTimezone, timezoneFormat } = formatTimezoneCode(
    timezone,
    deviceTimezone
  );

  const showTimezoneFormat = showTimezone ? timezoneFormat : "";

  const timezoneUsed =
    timezone && isDifferentTimezone ? timezone : deviceTimezone;

  return moment(datetime)
    .tz(timezoneUsed)
    .format(format + showTimezoneFormat);
};

const formatTimezoneCode = (timezone: string, deviceTimezone: string) => {
  const locationUtcOffset = timezone ? moment().tz(timezone).format("Z") : "";
  const deviceUtcOffset = moment().tz(deviceTimezone).format("Z");
  const isDifferentTimezone = locationUtcOffset !== deviceUtcOffset;
  const timezoneFormat = isDifferentTimezone ? " (z)" : "";

  return { isDifferentTimezone, timezoneFormat };
};

const goToPreviousPage = (navigate: any, url: string, enforceUrl = false) => {
  if (enforceUrl) {
    navigate(url);
  } else if (window.history.length < 2) {
    navigate(url);
  } else {
    window.history.back();
  }
};

const handleSessionCheck = () => {
  try {
    const userString = localStorage.getItem("user") || "{}";
    const user = JSON.parse(userString);

    // Check if user has the new structure (v2.0.0+)
    // v2.0.0 added a 'companies' array that wasn't in v0.1.9
    // If not present, clear old data and force re-login
    if (!user?.companies || !Array.isArray(user.companies)) {
      console.warn(
        "Outdated user data structure detected. Clearing localStorage and forcing re-login."
      );
      localStorage.clear();
      window.location.href = "/login";
      return;
    }

    // Ensure company.id is valid (not undefined or 0)
    if (!user?.company?.id || user.company.id === 0) {
      console.warn(
        "Invalid company data detected. Clearing localStorage and forcing re-login."
      );
      localStorage.clear();
      window.location.href = "/login";
      return;
    }

    apiRequest({
      method: "POST",
      endpoint: `v2/companies/${user.company.id}/health-check`,
      body: { method: CLIENT_LOGIN_METHOD },
    })
      .then((result) => {
        localStorage.setItem("user", JSON.stringify(result));
      })
      .catch((error) => {
        console.warn(
          "Error checking user session",
          formatApiRequestError(error)
        );
      });
  } catch (error) {
    console.error("Error parsing user data from localStorage:", error);
    localStorage.clear();
    window.location.href = "/login";
  }
};

const handleSwitchCompany = (companyId: number, navigate: any) => {
  // Validate companyId to prevent invalid API calls
  // Treat 0, null, undefined, or any falsy value as invalid
  if (!companyId) {
    console.warn("Invalid companyId provided to handleSwitchCompany");
    return;
  }

  apiRequest({
    method: "POST",
    endpoint: `v2/companies/${companyId}/health-check`,
    body: { method: CLIENT_LOGIN_METHOD },
  })
    .then((result) => {
      localStorage.setItem("user", JSON.stringify(result));
      navigate("/messaging");
      // force reload because of stream
      window.location.reload();
    })
    .catch((error) => {
      console.warn("Error checking user session", formatApiRequestError(error));
    });
};

const parseCountryFromPhoneNumber = (phoneNumber: string) => {
  const phoneUtil = PhoneNumberUtil.getInstance();
  if (phoneNumber) {
    try {
      const parsedNumber = phoneUtil.parse(phoneNumber);
      return phoneUtil.getRegionCodeForNumber(parsedNumber);
    } catch (error) {
      return "US";
    }
  }
  return "US";
};

const redirectToLogout = (navigate: any) => {
  setTimeout(() => {
    navigate("/login");
  }, 1000);
};

const resolveBrowserLocale = (defaultLocale = "en") => {
  /*
    Rely on the window.navigator object to determine user locale
    from http://blog.ksol.fr/user-locale-detection-browser-javascript/
  */
  const { language } = window.navigator;
  return (language || defaultLocale).split("-")[0];
};

const sanitizeAscii = (text: string) => {
  return text
    .replace(/[^\u0020-\u007E]/g, "-")
    .replace(/ /g, "_")
    .toLowerCase();
};

const translateMessage = (
  translatedMessage: any,
  userLanguageSetting: string,
  hasUserTranslationPermission: boolean = true
) => {
  let showTranslateIcon = false;
  let isTranslated = false;
  if (translatedMessage.text) {
    const language = resolveBrowserLocale(userLanguageSetting);
    const hasCustomTranslation = !!translatedMessage.swept_i18n;
    const hasStreamTranslation = !!translatedMessage.i18n;
    const customTranslation = hasCustomTranslation
      ? translatedMessage.swept_i18n[`${language}_text`]
      : null;
    const streamTranslation = hasStreamTranslation
      ? translatedMessage.i18n[`${language}_text`]
      : null;
    if (hasUserTranslationPermission) {
      if (hasCustomTranslation && customTranslation) {
        showTranslateIcon =
          translatedMessage.swept_i18n.language &&
          language !== translatedMessage.swept_i18n.language;
        isTranslated = true;
        translatedMessage.text = customTranslation;
      } else if (hasStreamTranslation && streamTranslation) {
        showTranslateIcon =
          translatedMessage.i18n.language &&
          language !== translatedMessage.i18n.language;
        isTranslated = true;
        translatedMessage.text = streamTranslation;
      } else if (
        (hasCustomTranslation &&
          language !== translatedMessage.swept_i18n.language) ||
        (hasStreamTranslation && language !== translatedMessage.i18n.language)
      ) {
        showTranslateIcon = true;
      }
    }
  }
  return { translatedMessage, showTranslateIcon, isTranslated };
};

const validatePassword = (password: string) => {
  const errors = [];
  if (password.length < 6) {
    errors.push("min_length");
  }

  if (!password.match(/[A-Z]/)) {
    errors.push("upper_case");
  }

  if (!password.match(/[a-z]/)) {
    errors.push("lower_case");
  }

  if (!password.match(/[0-9]/)) {
    errors.push("number");
  }

  // see: https://www.owasp.org/index.php/Password_special_characters
  if (!password.match(/[ !"#$%&'()*+,-./:;<=>?@[\\\]^_`{|}~]/)) {
    errors.push("symbol");
  }

  return { isValid: errors.length === 0, errors };
};

const validatePhoneNumberForCountry = (
  value: string,
  country: string | undefined
) => {
  const phoneUtil = PhoneNumberUtil.getInstance();
  const results = {
    value: "",
    isValid: false,
    errors: [] as string[],
  };
  try {
    const number = phoneUtil.parse(value, country);
    if (phoneUtil.isValidNumberForRegion(number, country) === true) {
      results.value = phoneUtil.format(number, PhoneNumberFormat.E164);
      results.isValid = true;
      return results;
    }
  } catch (error: any) {
    results.errors.push(error);
  }
  return results;
};

export type BadgeCountsContextType = { [key: string]: any };
export type UserContextType = { [key: string]: any };

const BadgeCountsContext = createContext<BadgeCountsContextType>({});
const DrawerContext = createContext(true);
const UserContext = createContext<UserContextType>({});

export {
  apiDownload,
  apiRequest,
  clearAndLogout,
  customNavigate,
  encodeURLParams,
  formatApiRequestError,
  formatMomentWithTimezone,
  goToPreviousPage,
  handleSessionCheck,
  handleSwitchCompany,
  parseCountryFromPhoneNumber,
  redirectToLogout,
  resolveBrowserLocale,
  sanitizeAscii,
  translateMessage,
  validatePassword,
  validatePhoneNumberForCountry,
  BadgeCountsContext,
  DrawerContext,
  handlePaymentRequiredResponse,
  PAYMENT_REQUIRED_EVENT,
  UserContext,
};
